import { useEffect, useMemo, useState } from "react";
import { Delete, Phone, Repeat2 } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { listCallerIds, type CallerIdRow } from "../../lib/calls";
import { formatPhone, normalizeE164 } from "../../lib/phone";
import { Avatar, IconBtn } from "../ui";
import { PopHost } from "../shell/Pop";
import { DialerFoot, useDialFlags } from "./DialerFoot";
import { PhoneContextPanel } from "./context";
import { DialPicks } from "./dial/DialPicks";
import { useDialLists } from "./dial/useDialLists";
import { digitsOf, fmtTyped, matchTyped, type Candidate } from "./dial/candidate";
import type { DialFlags } from "./dialFlags";

const KEYS: { k: string; sub?: string }[] = [
  { k: "1" },
  { k: "2", sub: "ABC" },
  { k: "3", sub: "DEF" },
  { k: "4", sub: "GHI" },
  { k: "5", sub: "JKL" },
  { k: "6", sub: "MNO" },
  { k: "7", sub: "PQRS" },
  { k: "8", sub: "TUV" },
  { k: "9", sub: "WXYZ" },
  { k: "*" },
  { k: "0", sub: "+" },
  { k: "#" },
];

/**
 * Everything one press of the green button decides.
 *
 * An object rather than a growing argument list, because the pad now answers
 * several separate questions about a call - who to ring, what to place it with,
 * which of our own lines to show, and which company record it belongs to - and
 * a fourth positional argument is how the third one got dropped on the way out
 * in the first place.
 */
export interface DialRequest {
  number: string;
  companyName?: string;
  /**
   * The company's registry code, when the number came from a source that knows
   * it. Today that is the business register tab; it travels into the call log
   * so the call is filed against the company from the moment it is placed.
   */
  regCode?: string;
  /**
   * The website the development brain looked at, when the picked row knew it.
   *
   * Travels beside `regCode` rather than instead of it, because they answer
   * different questions: the code files the call against a company, the domain
   * names the SITE the pre-call briefing should read. Without it that briefing
   * falls back to "the newest brain run carrying this register code", which is
   * the wrong site as soon as a company has had two of them looked at.
   */
  domain?: string;
  flags?: DialFlags;
  /**
   * The line picked in the header pill. Omitted when the account has only the
   * one, or when the picker could not be loaded, which the server reads as the
   * account's own caller id.
   */
  fromNumber?: string;
}

/**
 * The dial pad.
 *
 * Two ways in, because operators arrive with two different things: a number
 * somebody read out (the keypad) and a company they mean to call back (the
 * column beside it). That column is real - recent calls, the client book and
 * the business register, searched live - rather than a static address book,
 * because the number worth calling is almost always one this CRM or the
 * register already knows.
 *
 * The pad also owns what the call is placed WITH - recorded, transcribed - and
 * hands it out with every dial, whichever of the four ways the number was
 * picked. Undefined while the settings are still loading, which the server reads
 * as "the account default", the same as a caller that never offers the choice.
 */
export function Dialer({
  prefill,
  canSearchRegister,
  onCall,
}: {
  prefill?: string;
  /** This operator may read the business register, so the third tab is drawn. */
  canSearchRegister: boolean;
  onCall: (req: DialRequest) => void;
}) {
  const t = useT();
  const flags = useDialFlags();
  const lists = useDialLists();
  const [value, setValue] = useState(prefill ? fmtTyped(prefill) : "+372 ");
  const [lines, setLines] = useState<{ rows: CallerIdRow[]; selected: string }>({ rows: [], selected: "" });
  const [lineIx, setLineIx] = useState(0);

  useEffect(() => {
    listCallerIds()
      .then((r) => {
        setLines({ rows: r.callerIds, selected: r.selected });
        const at = r.callerIds.findIndex((c) => c.phoneNumber === r.selected);
        setLineIx(at < 0 ? 0 : at);
      })
      .catch(() => {});
  }, []);

  // The match under the field is what turns a typed number into a name before
  // the call connects - the one moment it can still be corrected. Recent calls
  // are searched ahead of the client book: somebody rung yesterday outranks a
  // namesake three thousand rows into the address list. The register is not
  // searched here at all - it is a request per keystroke against 376 000 rows,
  // and the tab beside the field is where that question gets asked out loud.
  const match = useMemo(() => {
    const hit = matchTyped(value, [lists.recent, lists.contacts]);
    if (hit) return { name: hit.name, detail: hit.detail };
    // Four digits before admitting to not knowing them. Three is enough to pick
    // somebody out of a list; it is not enough to say with a straight face that
    // nobody in the CRM has this number.
    return digitsOf(value).length >= 4
      ? { name: t("calls.dial.unknown"), detail: t("calls.dial.noContact") }
      : null;
  }, [value, lists.recent, lists.contacts, t]);

  const dialable = normalizeE164(value);
  const line = lines.rows[lineIx];

  // One door out of the pad, so the footer's answer and the header's line
  // cannot be attached to three of the four ways to dial and forgotten on the
  // fourth. The line travels on every call rather than only on a switched one:
  // the server compares it against the account default anyway, and "only send
  // it when it changed" is a rule the next caller here would have to rediscover.
  const place = (number: string, from?: Candidate) =>
    onCall({
      number,
      // `companyName` and NOT `name`. The column's name falls back to a
      // formatted phone number so every row has something to draw; sending that
      // filed the call under a company called "+372 5123 4567".
      companyName: from?.companyName,
      regCode: from?.regCode,
      // Whichever source knew the site: the register tab from its brain badge,
      // a recent call from the identity the original call was placed with.
      // Undefined for a typed number.
      domain: from?.domain,
      flags: flags.effective ?? undefined,
      fromNumber: line?.phoneNumber,
    });

  return (
    // The pad mounts no `PopHost` of its own anywhere above it - the call window
    // is a portal next to <body> - and a `Pop` without one is a control that
    // silently never opens. The footer's device picker is the only one so far.
    <PopHost>
      {/* `crm-dhead` so the phone rules can let this row wrap: the title plus a
          full caller-id pill is wider than a 327px dialog, and the close button
          is parked over its right end. */}
      <div className="crm-dhead flex flex-none items-center gap-3.5 border-b border-[var(--crm-hair)] px-[26px] py-[22px] pb-[18px]">
        <span className="crm-m-t">{t("calls.dial.title")}</span>
        {lines.rows.length > 0 ? (
          /* `w-auto` is load-bearing. `.crm-fold` is the rail row shape, and
             `button.crm-fold` in index.css carries `width: 100%` so a rail row
             fills its column - a button gives up the block-level stretch that
             rule replaces. Here the same class is a pill sitting beside a title,
             and inherited unchecked it drew the sunk background across the whole
             head and 50px past the dialog's right edge, under the close disc.
             `flex-none` does not undo it: `flex: none` leaves the basis at
             `auto`, which is this `width`. The utility layer comes after
             `@layer components`, so this wins. */
          <button
            type="button"
            className="crm-fold h-[38px] w-auto flex-none bg-[var(--crm-sunk)] px-[15px] text-[12.25px] font-semibold"
            title={t("calls.dial.line")}
            onClick={() => setLineIx((i) => (i + 1) % lines.rows.length)}
          >
            <i className="size-[7px] rounded-full bg-[var(--color-primary)]" />
            <b className="font-bold tabular-nums text-foreground">
              {line ? formatPhone(line.phoneNumber) : lines.selected}
            </b>
            {lines.rows.length > 1 ? <Repeat2 className="size-3.5" /> : null}
          </button>
        ) : null}
      </div>

      {/* `flex-1`, and the window has no height of its own to hand out - so on
          any screen tall enough this resolves to the grid's own content, which
          is what the keypad column below asks for. It earns its keep on a short
          one: `.crm-modal`'s `max-height: 100%` clamps the card, and a basis of
          zero is what lets the grid give the missing pixels back rather than
          push the footer off the bottom. The two columns then scroll inside
          themselves, which is what both of them are built to do. */}
      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[330px_minmax(0,1fr)]">
        <div className="flex min-h-0 flex-col px-[26px] pb-6 pt-[22px]">
          {/* The green button is NOT in this box, and that is the rule the call
              window's red one already follows: on a laptop short enough to
              squeeze the pad, the one control everybody is reaching for must not
              be the thing that scrolled away. Everything above it may scroll; it
              may not. The negative margin is the scrollbar's gutter - the field
              and the keys have rounded backgrounds a scroll box would otherwise
              shave a hair off. */}
          <div className="-mx-2 flex min-h-0 flex-1 flex-col overflow-y-auto px-2">
            <div className="flex h-[60px] flex-none items-center gap-1.5 rounded-full bg-[var(--crm-sunk)] pl-5 pr-2.5">
              <input
                value={value}
                onChange={(e) => setValue(fmtTyped(e.target.value))}
                type="tel"
                inputMode="tel"
                placeholder={t("calls.dial.placeholder")}
                autoComplete="off"
                spellCheck={false}
                className="min-w-0 flex-1 border-0 bg-transparent text-[22px] font-bold tracking-[-0.03em] tabular-nums outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && dialable) place(dialable);
                }}
              />
              <IconBtn
                className="border-0"
                title={t("calls.dial.backspace")}
                aria-label={t("calls.dial.backspace")}
                onClick={() => setValue((v) => fmtTyped(v.replace(/[^\d+]/g, "").slice(0, -1)))}
              >
                <Delete className="size-4" />
              </IconBtn>
            </div>

            {/* Fixed height whether or not a contact matches, so the keypad below
                never shifts while the number is being typed. */}
            <div className="my-3 flex min-h-10 flex-none items-center gap-2.5 px-1 text-[12.25px] text-muted-foreground">
              {match ? (
                <>
                  <Avatar name={match.name} size={32} />
                  <span className="min-w-0">
                    <b className="block text-[12.75px] font-bold text-foreground">{match.name}</b>
                    <span className="crm-trunc block">{match.detail}</span>
                  </span>
                </>
              ) : null}
            </div>

            {/* No `mt-auto` here any more, and its absence is the fix rather
                than a tidy-up. The window used to be pinned at 700px, which is
                taller than a field, a match line, twelve keys and a green
                button; that rule pushed the whole surplus into one gap between
                the number and the first row of keys - roughly 77px of nothing,
                on every screen. The window is the keypad's height now (see
                `.crm-modal` in index.css), so there is no surplus to place. */}
            <div className="grid flex-none grid-cols-3 gap-2.5">
              {KEYS.map((key) => (
                <button
                  key={key.k}
                  type="button"
                  className="crm-key"
                  onClick={() => setValue((v) => fmtTyped(v + key.k))}
                >
                  <b>{key.k}</b>
                  <span>{key.sub ?? ""}</span>
                </button>
              ))}
            </div>

            {/* BELOW the keypad rather than under the number, which is where the
                plan put it. The briefing appears the moment what is typed
                becomes dialable, which is several digits before the operator is
                finished - between the field and the keys it would shove all
                twelve of them down under a finger already on its way to the next
                one. Down here it grows into the scroll this column already has,
                and the green button is outside that scroll, so nothing anybody
                is reaching for moves. */}
            <PhoneContextPanel number={dialable} className="mt-5 flex-none" />
          </div>

          <button
            type="button"
            className="crm-callbtn flex-none"
            disabled={!dialable}
            onClick={() => dialable && place(dialable)}
          >
            <Phone className="size-4" />
            {t("calls.dial.call")}
          </button>
        </div>

        <DialPicks
          lists={lists}
          canSearchRegister={canSearchRegister}
          onPick={(row) => setValue(fmtTyped(row.number))}
          onCall={(row) => {
            const n = normalizeE164(row.number);
            if (n) place(n, row);
          }}
        />
      </div>

      <DialerFoot flags={flags} />
    </PopHost>
  );
}
